(() => {
  if (customElements.get('cart-drawer')) return;
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('keyup', event => { if (event.code === 'Escape') this.close(); });
      this.addEventListener('click', event => { if (event.target.closest('#CartDrawer-Overlay')) this.close(); });
      this.headerClick = event => {
        const link = event.target.closest('a');
        if (!this.isHeaderCart(link) || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
        event.preventDefault();
        this.open(link);
        this.refresh().catch(error => console.warn('Cart refresh unavailable', error));
      };
      this.headerKey = event => {
        if (event.code === 'Space' && this.isHeaderCart(event.target)) {
          event.preventDefault();
          event.target.click();
        }
      };
      this.cartChanged = event => { if (event.detail?.cart) this.updateCount(event.detail.cart.item_count); };
      this.decorateHeader = () => this.headerLinks().forEach(link => {
        link.setAttribute('aria-haspopup', 'dialog');
        link.setAttribute('aria-controls', 'CartDrawer');
      });
    }
    connectedCallback() {
      this.decorateHeader();
      document.addEventListener('click', this.headerClick);
      document.addEventListener('keydown', this.headerKey);
      document.addEventListener('cart:updated', this.cartChanged);
      document.addEventListener('shopify:section:load', this.decorateHeader);
      // PPO dialogs live outside the drawer: transfer focus and restore it on close.
      this.modalObserver = new MutationObserver(() => this.syncModalFocus());
      this.modalObserver.observe(document.body, { childList: true });
      // PPO mounts its real buttons asynchronously, including after cart refreshes.
      // Move the existing nodes so their editor and duplicate handlers stay attached.
      this.lineActionsObserver = new MutationObserver(() => this.placeLineActions());
      this.lineActionsObserver.observe(this, { childList: true, subtree: true });
      this.placeLineActions();
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.headerClick);
      document.removeEventListener('keydown', this.headerKey);
      document.removeEventListener('cart:updated', this.cartChanged);
      document.removeEventListener('shopify:section:load', this.decorateHeader);
      this.modalObserver?.disconnect();
      this.lineActionsObserver?.disconnect();
      if (this.classList.contains('active')) this.close();
    }
    isHeaderCart(link) {
      return link instanceof HTMLAnchorElement &&
        (link.id === 'cart-icon-bubble' || (link.closest('[data-ch-root]') && new URL(link.href).pathname === routes.cart_url));
    }
    headerLinks() {
      return [...document.querySelectorAll('#cart-icon-bubble, [data-ch-root] a')].filter(link => this.isHeaderCart(link));
    }
    placeLineActions() {
      this.querySelectorAll('.cart-item').forEach(item => {
        const slot = item.querySelector('[data-coverly-drawer-line-actions]');
        if (!slot) return;
        item.querySelectorAll('.coverly-cart-actions').forEach(actions => {
          if (actions.parentElement !== slot) slot.append(actions);
        });
        const toggleSlot = item.querySelector('[data-coverly-drawer-line-toggle]');
        const toggle = item.querySelector('.coverly-line-toggle');
        if (toggleSlot && toggle && toggle.parentElement !== toggleSlot) toggleSlot.append(toggle);
      });
    }
    updateCount(count) {
      if (!Number.isFinite(count)) return;
      this.headerLinks().forEach(link => {
        if (!link.closest('[data-ch-root]')) return;
        let badge = link.querySelector('.ch__cart-count');
        if (!badge && count > 0) {
          badge = document.createElement('span');
          badge.className = 'ch__cart-count';
          (link.querySelector('.ch__util-ico') || link).append(badge);
        }
        if (badge) { badge.textContent = String(count); badge.hidden = count === 0; }
      });
    }
    syncModalFocus() {
      if (!this.classList.contains('active')) return;
      const modal = document.querySelector('.coverly-share-overlay, .coverly-edit-overlay, dialog.coverly-cart-3d[open]');
      if (modal === this.ppoModal) return;
      const inner = this.querySelector('.drawer__inner');
      this.ppoModal = modal;
      inner.inert = !!modal;
      if (modal) {
        this.returnFromModal = this.contains(document.activeElement) ? document.activeElement : null;
        if (modal.matches('dialog.coverly-cart-3d')) removeTrapFocus();
        else trapFocus(modal, modal.querySelector('button, input, [tabindex="0"]') || modal);
      } else {
        trapFocus(inner, this.returnFromModal?.isConnected ? this.returnFromModal : inner.querySelector('.drawer__close'));
      }
    }
    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      if (this.classList.contains('active')) return;
      this.wasBodyLocked = document.body.classList.contains('overflow-hidden');
      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
      document.body.classList.add('coverly-cart-drawer-open');
      const inner = this.querySelector('.drawer__inner');
      trapFocus(inner, inner.querySelector('.drawer__close'));
      document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    }
    close() {
      this.classList.remove('active');
      document.body.classList.remove('coverly-cart-drawer-open');
      this.querySelector('.drawer__inner').inert = false;
      this.ppoModal = null;
      removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : null);
      if (!this.wasBodyLocked) document.body.classList.remove('overflow-hidden');
    }
    async refresh() {
      if (this.refreshing) return this.refreshing;
      this.refreshing = (async () => {
        const url = new URL(routes.cart_url, location.origin);
        url.searchParams.set('sections', this.getSectionsToRender().map(section => section.id).join(','));
        const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(10000) });
        if (!response.ok) throw new Error('Cart sections unavailable');
        this.renderContents({ sections: await response.json() }, false);
      })();
      try { await this.refreshing; } finally { this.refreshing = null; }
    }
    renderContents(state, shouldOpen = true) {
      const html = state.sections?.['cart-drawer'];
      const content = html && new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
      if (!content?.querySelector('.drawer__inner')) throw new Error('Cart drawer section unavailable');
      const count = Number(content.querySelector('.drawer__inner').dataset.cartCount);
      this.classList.toggle('is-empty', count === 0);
      this.querySelector('#CartDrawer').innerHTML = content.innerHTML;
      const icon = document.getElementById('cart-icon-bubble');
      if (icon && state.sections['cart-icon-bubble']) {
        const parsed = new DOMParser().parseFromString(state.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
        if (parsed) icon.innerHTML = parsed.innerHTML;
      }
      this.updateCount(count);
      if (this.classList.contains('active')) {
        const inner = this.querySelector('.drawer__inner');
        trapFocus(inner, inner.querySelector('.drawer__close'));
      }
      if (shouldOpen) this.open();
    }
    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '#CartDrawer' }, ...(document.getElementById('cart-icon-bubble') ? [{ id: 'cart-icon-bubble' }] : [])];
    }
    setActiveElement(element) { this.activeElement = element; }
  }
  customElements.define('cart-drawer', CartDrawer);
  class CartDrawerItems extends CartItems {
    onCartUpdate() {
      this.closest('cart-drawer').refresh().catch(error => console.warn('Cart refresh unavailable', error));
    }
    getSectionsToRender() {
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        ...(document.getElementById('cart-icon-bubble') ? [{ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }] : [])
      ];
    }
  }
  customElements.define('cart-drawer-items', CartDrawerItems);
})();
